import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { FormRow, FormSection, MaskedInput, Select, TextInput } from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { STATES } from '~/constants';
import type { profileFormMachine } from './profileForm.machine';
import { SensitiveInfoSchema, SensitiveInfoValues } from './types';
import { useProfileDraftForm, type ProfileDrafts } from './useProfileDraftForm';

// [MCV-1905] A 'None' first option (value '') lets the issuing state be cleared; the
// shared STATES list is left alone because address forms use it too.
const LICENSE_STATE_OPTIONS = [{ label: 'None', value: '' }, ...STATES];

const TAGS = CT.profile.sensitiveInfo.form;

interface SensitiveProps {
  values: Partial<SensitiveInfoValues>;
  actor: ActorRefFrom<typeof profileFormMachine>;
  drafts?: ProfileDrafts;
}

export const SensitiveInformationForm = ({ actor, drafts }: SensitiveProps) => {
  const { sensitiveInfo } = useSelector(actor, (state) => state.context);
  // `values` (vs `defaultValues`) is reactive — RHF reruns reset internally when
  // the machine rehydrates sensitiveInfo after a save, so fields reflect the
  // saved state without a page refresh.
  const methods = useProfileDraftForm<SensitiveInfoValues>(
    {
      resolver: zodResolver(SensitiveInfoSchema),
      values: sensitiveInfo,
    },
    drafts?.sensitive
  );
  const { handleSubmit } = methods;

  const onSubmitSuccess = async (values: SensitiveInfoValues) =>
    actor.send({ type: 'ON_SUBMIT_SENSITIVE', values });

  const onSubmitError = (err: unknown) =>
    console.debug('SensitiveInformationForm validation error:', err);

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-5"
        data-cy={TAGS.id}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
      >
        <FormSection componentsPerLine={2}>
          <FormRow>
            <TextInput
              name={'driversLicenseNumEnc'}
              cyTag={TAGS.driversNumInput}
              label={'Drivers License'}
              placeholder={'STO000000'}
              maxLength={20}
            />
          </FormRow>
          <FormRow>
            <Select
              wrapValueOnMobile
              name={'driversLicenseState'}
              label={'Drivers License Issuing State'}
              cyTag={TAGS.driversStateSelect}
              options={LICENSE_STATE_OPTIONS}
            />
          </FormRow>
        </FormSection>
        <FormSection componentsPerLine={2}>
          <FormRow>
            <MaskedInput
              name={'ssnEnc'}
              cyTag={TAGS.socialSecurityNumInput}
              label={'Social Security Number'}
              placeholder="***-**-****"
              mask={'###-##-####'}
              replacement={{ '#': /\d/ }}
            />
          </FormRow>
          <FormRow>
            <TextInput
              name={'passportNumberEnc'}
              cyTag={TAGS.passportNum}
              label={'Passport Number'}
              maxLength={20}
            />
          </FormRow>
        </FormSection>

        <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
      </form>
    </FormProvider>
  );
};
